import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Download, Link2, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useData } from '../App';
import { Badge, Button, Card, Input, Modal, SearchSelect, Select } from '../components/UI';
import { api } from '../services/api';
import { ADDRESS_STATE, AddressTable } from '../components/AddressTable';
import { formatDay, todayISO } from '../utils/format';
import { errorText, Money, money, StatusBadge, Td, Th, useBillingRefs, useNames } from '../components/billing/shared';
import { AddressLedger, AddressRow, AddressState, LanComment, LanComments, LanMapping, LanRequest, LanStatus, LanType, Project, RateRole, RealizationVolumes,
         VolumePayout, VolumeSettings, VolumeSummary, WorkRate } from '../types';
import { confirmDialog } from '../components/Dialogs';

const STATUS: Record<LanStatus, { label: string; variant: 'success' | 'warning' | 'danger' | 'neutral' }> = {
    NEW: { label: 'Ждёт реализации', variant: 'warning' },
    REVIEW: { label: 'Нужен разбор', variant: 'danger' },
    FREE: { label: 'Бесплатно', variant: 'neutral' },
    LINKED: { label: 'В реализации', variant: 'success' },
    CANCELLED: { label: 'Снята в CRM', variant: 'neutral' },
};
const MODES = [{ value: 'ITEMS', label: 'По позициям прайса' }, { value: 'MAN_DAY', label: 'Человеко-дни' }, { value: 'MANUAL', label: 'Разбор вручную' }];
const ROLES: { value: string; label: string }[] = [
    { value: '', label: '—' }, { value: 'PANEL', label: 'Панель' }, { value: 'FIRMWARE', label: 'Прошивка' },
    { value: 'LINK', label: 'Линк' }, { value: 'BUFFER', label: 'Буфер (добивка суммы)' }];
const SOURCES: Record<string, string> = { request_param: 'параметр заявки CRM', warehouse: 'склад', system: 'система', manual: 'вручную' };
const TABS = [
    { key: 'requests', label: 'Заявки' }, { key: 'addresses', label: 'По адресам' }, { key: 'pool', label: 'Реализации и пул' },
    { key: 'rates', label: 'Прайс объекта' }, { key: 'report', label: 'Сверка' }, { key: 'settings', label: 'Настройки CRM' },
] as const;
type Tab = typeof TABS[number]['key'];

const qtyText = (n: number | null | undefined) => (n ?? 0).toLocaleString('ru-RU', { maximumFractionDigits: 3 });
const worksText = (r: LanRequest) => r.works.filter(w => w.code).map(w => `${w.name || w.code}: ${qtyText(w.qty)}`).join(', ');

const useProjects = () => {
    const { revision } = useData();
    const [projects, setProjects] = useState<Project[]>([]);
    useEffect(() => { api.getProjects().then(r => setProjects(r.items)).catch(() => {}); }, [revision]);
    return projects;
};

// ---------------------------------------------------------------------------
// Заявки: сырые объёмы, разбор, реализация из накопленного
// ---------------------------------------------------------------------------
// Комментарии заявки из CRM: первый — что поручили сделать, закрытия — что сделали
const CommentBox: React.FC<{ c: LanComment; title: string; tone: string }> = ({ c, title, tone }) => (
    <div className={`rounded-md border px-3 py-2 ${tone}`}>
        <div className="text-xs text-slate-500">{title} · {c.author || '—'}{c.createdAt ? ` · ${formatDay(c.createdAt.slice(0, 10))} ${c.createdAt.slice(11, 16)}` : ''}
            {c.photos > 0 && ` · фото: ${c.photos}`}</div>
        <div className="whitespace-pre-line break-words text-slate-800">{c.text || <span className="text-slate-400">без текста</span>}</div>
    </div>
);

const CrmComments: React.FC<{ data: LanComments | null; all: boolean; onAll: () => void }> = ({ data, all, onAll }) => {
    if (!data) return <div className="text-xs text-slate-400">Загружаю комментарии из CRM…</div>;
    if (!data.available) return <div className="text-xs text-amber-700">{data.error || 'Комментарии из CRM недоступны'}</div>;
    const list = data.comments;
    if (!list.length) return <div className="text-xs text-slate-400">В CRM нет комментариев к заявке</div>;
    const first = list.find(c => !c.isClosing);
    const closing = list.filter(c => c.isClosing);
    const shown = all ? list : [first, ...closing].filter((c): c is LanComment => !!c);
    const rest = list.length - shown.length;
    return (
        <div className="space-y-2">
            {shown.map(c => <CommentBox key={c.id} c={c}
                title={c.isClosing ? 'Закрытие заявки' : c === first ? 'Задание' : 'Комментарий'}
                tone={c.isClosing ? 'border-emerald-200 bg-emerald-50' : c === first ? 'border-blue-200 bg-blue-50' : 'border-slate-200 bg-slate-50'} />)}
            {rest > 0 && <button className="text-xs text-blue-600 hover:underline" onClick={onAll}>Показать все комментарии ({list.length})</button>}
        </div>
    );
};

const ReviewForm: React.FC<{ req: LanRequest; projects: Project[]; rates: WorkRate[]; onClose: () => void; onSaved: () => void }> =
    ({ req, projects, rates, onClose, onSaved }) => {
    const { showNotification } = useData();
    const [projectId, setProjectId] = useState(req.projectId || '');
    const [amount, setAmount] = useState(req.manualAmount != null ? String(req.manualAmount) : '');
    const [free, setFree] = useState(req.status === 'FREE');
    const [comment, setComment] = useState(req.comment || '');
    const [crm, setCrm] = useState<LanComments | null>(null);
    const [allComments, setAllComments] = useState(false);
    useEffect(() => {
        if (!req.externalTaskId) return;
        api.lanRequestComments(req.id).then(setCrm).catch(e => setCrm({ available: false, error: errorText(e), comments: [] }));
    }, [req.id, req.externalTaskId]);
    const submit = async () => {
        try {
            await api.reviewLanRequest(req.id, { projectId: projectId || null, manualAmount: amount === '' ? null : Number(amount),
                                                 free, comment: comment || null });
            onSaved(); onClose();
        } catch (e) { showNotification(errorText(e), 'error'); }
    };
    return (
        <Modal isOpen onClose={onClose} title={`Разбор заявки №${req.number || '—'} от ${formatDay(req.workDate)}`}>
            <div className="space-y-3 text-sm">
                <div className="text-slate-600">{req.typeName} · {req.address || 'без адреса'} · бригада {req.crewSize || 0} чел.</div>
                {req.externalTaskId && <CrmComments data={crm} all={allComments} onAll={() => setAllComments(true)} />}
                {req.works.length > 0 && <div className="text-xs text-slate-500">Работы: {req.works.map(w => `${w.sourceName} ${qtyText(w.sourceQty)}`).join(', ')}</div>}
                <SearchSelect label="Объект" value={projectId} onChange={setProjectId}
                    options={[{ value: '', label: 'Не выбран' }, ...projects.map(p => ({ value: p.id, label: p.name }))]} />
                <label className="flex items-center gap-2"><input type="checkbox" checked={free} onChange={e => setFree(e.target.checked)} />
                    Бесплатный выезд — без долга заказчика</label>
                {!free && <Input label="Сумма заказчику вручную, ₽ с НДС (пусто — по прайсу/режиму типа)" type="number" step="0.01" min="0"
                                 value={amount} onChange={e => setAmount(e.target.value)} />}
                <Input label="Комментарий" value={comment} onChange={e => setComment(e.target.value)} />
                {projectId && rates.length === 0 && <p className="text-xs text-slate-500">Прайс объекта заполнится при первом открытии вкладки «Прайс объекта».</p>}
                <div className="flex justify-end gap-2 pt-2 border-t">
                    <Button variant="secondary" size="sm" onClick={onClose}>Отмена</Button>
                    <Button size="sm" onClick={submit}>Сохранить</Button>
                </div>
            </div>
        </Modal>
    );
};

const ManualVolumeForm: React.FC<{ projectId: string; rates: WorkRate[]; onClose: () => void; onSaved: () => void }> =
    ({ projectId, rates, onClose, onSaved }) => {
    const { showNotification } = useData();
    const [date, setDate] = useState(todayISO());
    const [qty, setQty] = useState<Record<string, string>>({});
    const [comment, setComment] = useState('');
    const submit = async () => {
        const works = Object.entries(qty).filter(([, v]) => Number(v) > 0).map(([code, v]) => ({ code, qty: Number(v) }));
        if (!works.length) { showNotification('Укажите количество хотя бы по одной позиции', 'error'); return; }
        try { await api.addManualVolume({ projectId, workDate: date, works, manualAmount: null, comment: comment || null }); onSaved(); onClose(); }
        catch (e) { showNotification(errorText(e), 'error'); }
    };
    return (
        <Modal isOpen onClose={onClose} title="Объём вручную (без вебхука)">
            <div className="space-y-3 text-sm">
                <Input label="Дата работ" type="date" value={date} onChange={e => setDate(e.target.value)} />
                <div className="space-y-1">
                    {rates.filter(r => r.role !== 'BUFFER').map(r => (
                        <div key={r.code} className="flex items-center gap-2">
                            <span className="flex-1">{r.name}</span>
                            <input type="number" min="0" step="0.001" className="w-28 rounded-md border border-slate-300 px-2 py-1 text-right"
                                   value={qty[r.code] || ''} onChange={e => setQty(q => ({ ...q, [r.code]: e.target.value }))} />
                            <span className="w-10 text-slate-500">{r.unit}</span>
                        </div>
                    ))}
                </div>
                <Input label="Комментарий" value={comment} onChange={e => setComment(e.target.value)} placeholder="ручная корректировка / работы до запуска интеграции" />
                <div className="flex justify-end gap-2 pt-2 border-t">
                    <Button variant="secondary" size="sm" onClick={onClose}>Отмена</Button>
                    <Button size="sm" onClick={submit}>Добавить</Button>
                </div>
            </div>
        </Modal>
    );
};

const RealizeForm: React.FC<{ project: Project; ids: string[]; realizations: RealizationVolumes[]; onClose: () => void; onSaved: () => void }> =
    ({ project, ids, realizations, onClose, onSaved }) => {
    const { showNotification } = useData();
    const { contracts } = useBillingRefs();
    const names = useNames();
    const [mode, setMode] = useState<'new' | 'link'>('new');
    const [contractId, setContractId] = useState('');
    const [date, setDate] = useState(todayISO());
    const [target, setTarget] = useState('');
    const sale = contracts.filter(c => !project.customerId || c.counterpartyId === project.customerId);
    const submit = async () => {
        try {
            if (mode === 'new') {
                if (!contractId) { showNotification('Выберите договор — он определяет ИП', 'error'); return; }
                await api.realizationFromRequests({ contractId, date, projectId: project.id, requestIds: ids });
            } else {
                if (!target) { showNotification('Выберите реализацию', 'error'); return; }
                await api.linkRequests(target, ids);
            }
            onSaved(); onClose();
        } catch (e) { showNotification(errorText(e), 'error'); }
    };
    return (
        <Modal isOpen onClose={onClose} title={`Реализация по ${ids.length} заявк${ids.length === 1 ? 'е' : 'ам'}`}>
            <div className="space-y-3 text-sm">
                <div className="flex gap-4">
                    <label className="flex items-center gap-1"><input type="radio" checked={mode === 'new'} onChange={() => setMode('new')} /> Новая реализация</label>
                    <label className="flex items-center gap-1"><input type="radio" checked={mode === 'link'} onChange={() => setMode('link')} /> Добавить к существующей</label>
                </div>
                {mode === 'new' ? <>
                    <Select label="Договор (ИП, который получил оплату)" value={contractId} onChange={e => setContractId(e.target.value)}
                        options={[{ value: '', label: 'Выберите…' }, ...sale.map(c => ({ value: c.id, label: `${names.orgShort(c.organizationId)} · №${c.number} от ${formatDay(c.date)} · ${names.cp(c.counterpartyId)}` }))]} />
                    <Input label="Дата" type="date" value={date} onChange={e => setDate(e.target.value)} />
                    <p className="text-xs text-slate-500">В документ для заказчика попадут только позиции с ценой заказчика (кол-во × цена, с НДС), заявки
                        по человеко-дням и с ручной суммой — отдельными строками. Весь физический объём (и неоплачиваемые позиции) уходит в квоту ИП по объекту.
                        Суммы и объём можно поправить после сверки.</p>
                </> : (
                    <Select label="Реализация объекта" value={target} onChange={e => setTarget(e.target.value)}
                        options={[{ value: '', label: 'Выберите…' }, ...realizations.map(r => ({ value: r.id, label: `${names.orgShort(r.organizationId)} · №${r.number} от ${formatDay(r.date)} · ${money(r.gross)} ₽` }))]} />
                )}
                <div className="flex justify-end gap-2 pt-2 border-t">
                    <Button variant="secondary" size="sm" onClick={onClose}>Отмена</Button>
                    <Button size="sm" onClick={submit}>Привязать</Button>
                </div>
            </div>
        </Modal>
    );
};

const RequestsTab: React.FC<{ project: Project | null; projects: Project[] }> = ({ project, projects }) => {
    const { showNotification } = useData();
    const [rows, setRows] = useState<LanRequest[]>([]);
    const [review, setReview] = useState<LanRequest[]>([]);
    const [status, setStatus] = useState('NEW');
    const [summary, setSummary] = useState<VolumeSummary | null>(null);
    const [realizations, setRealizations] = useState<RealizationVolumes[]>([]);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [editing, setEditing] = useState<LanRequest | null>(null);
    const [manual, setManual] = useState(false);
    const [realize, setRealize] = useState(false);
    const load = useCallback(() => {
        api.lanRequests({ status: 'REVIEW' }).then(setReview).catch(() => {});
        if (!project) return;
        api.lanRequests({ project_id: project.id, status: status || undefined }).then(setRows).catch(e => showNotification(errorText(e), 'error'));
        api.volumeSummary(project.id).then(setSummary).catch(() => {});
        api.volumeRealizations(project.id).then(setRealizations).catch(() => {});
        setSelected(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project, status]);
    useEffect(load, [load]);
    const toggle = (id: string) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const selectable = rows.filter(r => r.status === 'NEW' || r.status === 'FREE');
    const remove = async (r: LanRequest) => {
        if (!(await confirmDialog('Удалить запись объёма?'))) return;
        try { await api.deleteLanRequest(r.id); load(); } catch (e) { showNotification(errorText(e), 'error'); }
    };
    const rateName = (code: string) => summary?.rates.find(r => r.code === code)?.name || code;
    return (
        <div className="space-y-4">
            {review.length > 0 && (
                <Card title={`Нужен разбор: ${review.length}`}>
                    <p className="text-xs text-slate-500 mb-2">Заявки без объекта или без способа расчёта (например, «Стандартная»): укажите объект и сумму
                        или отметьте выезд бесплатным — молча объект не подставляется.</p>
                    <div className="overflow-x-auto"><table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-500"><tr><Th>Дата</Th><Th>№</Th><Th>Тип</Th><Th>Адрес</Th><Th>Бригада</Th><Th /></tr></thead>
                        <tbody>{review.map(r => (
                            <tr key={r.id} className="border-t">
                                <Td>{formatDay(r.workDate)}</Td><Td>{r.number}</Td><Td>{r.typeName}</Td><Td className="max-w-xs truncate">{r.address}</Td>
                                <Td>{r.crewSize || 0} чел.</Td>
                                <Td right><Button size="sm" variant="outline" onClick={() => setEditing(r)}>Разобрать</Button></Td>
                            </tr>))}</tbody>
                    </table></div>
                </Card>
            )}
            {!project ? <p className="text-sm text-slate-500">Выберите объект.</p> : <>
                {summary && (
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                        <Card><div className="text-xs text-slate-500">Ждут реализации</div><div className="text-xl font-semibold">{summary.counts.NEW}</div></Card>
                        <Card><div className="text-xs text-slate-500">В реализациях</div><div className="text-xl font-semibold">{summary.counts.LINKED}</div></Card>
                        <Card><div className="text-xs text-slate-500">Оценка долга заказчика (без реализации)</div><div className="text-xl font-semibold"><Money value={summary.expectedClientDebt} /> ₽</div></Card>
                        <Card><div className="text-xs text-slate-500">Ждёт реализации по позициям</div>
                            <div className="text-xs">{Object.entries(summary.waiting).map(([c, q]) => `${rateName(c)}: ${qtyText(q)}`).join(', ') || '—'}</div></Card>
                    </div>
                )}
                <div className="flex flex-wrap items-end gap-2">
                    <div className="w-56"><Select label="Статус" value={status} onChange={e => setStatus(e.target.value)}
                        options={[{ value: '', label: 'Все' }, ...(Object.keys(STATUS) as LanStatus[]).map(s => ({ value: s, label: STATUS[s].label }))]} /></div>
                    <Button size="sm" disabled={!selected.size} onClick={() => setRealize(true)}><Link2 size={14} className="mr-1" /> В реализацию ({selected.size})</Button>
                    <Button size="sm" variant="outline" onClick={() => setManual(true)}><Plus size={14} className="mr-1" /> Объём вручную</Button>
                </div>
                <div className="overflow-x-auto bg-white border rounded-lg"><table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500"><tr>
                        <Th><input type="checkbox" checked={selectable.length > 0 && selected.size === selectable.length}
                                   onChange={e => setSelected(e.target.checked ? new Set(selectable.map(r => r.id)) : new Set())} /></Th>
                        <Th>Дата</Th><Th>№</Th><Th>Адрес</Th><Th>Объём</Th><Th right>Заказчику, ₽</Th><Th>Статус</Th><Th />
                    </tr></thead>
                    <tbody>{rows.map(r => (
                        <tr key={r.id} className="border-t align-top">
                            <Td>{(r.status === 'NEW' || r.status === 'FREE') && <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} />}</Td>
                            <Td className="whitespace-nowrap">{formatDay(r.workDate)}</Td>
                            <Td>{r.number || <span className="text-slate-400">вручную</span>}</Td>
                            <Td className="max-w-[16rem]">{r.address}<div className="text-xs text-slate-400">{r.brigade.map(b => b.fio).join(', ')}</div></Td>
                            <Td className="text-xs">{worksText(r) || '—'}
                                {r.works.some(w => !w.code) && <div className="text-amber-600">не учтено: {r.works.filter(w => !w.code).map(w => w.sourceName).join(', ')}</div>}</Td>
                            <Td right>{r.clientAmount == null ? '—' : <Money value={r.clientAmount} />}</Td>
                            <Td><Badge variant={STATUS[r.status].variant}>{STATUS[r.status].label}</Badge>
                                {r.changedAfterLink && <div className="text-xs text-red-600">изменилась в CRM после привязки</div>}</Td>
                            <Td right className="whitespace-nowrap">
                                {r.status !== 'LINKED' && <Button size="sm" variant="outline" onClick={() => setEditing(r)}>Разбор</Button>}
                                {!r.externalTaskId && r.status !== 'LINKED' && <button className="ml-2 text-slate-400 hover:text-red-600" onClick={() => remove(r)}><Trash2 size={14} /></button>}
                            </Td>
                        </tr>))}
                        {!rows.length && <tr><Td colSpan={8} className="text-center text-slate-400">Нет заявок</Td></tr>}
                    </tbody>
                </table></div>
            </>}
            {editing && <ReviewForm req={editing} projects={projects} rates={summary?.rates || []} onClose={() => setEditing(null)} onSaved={load} />}
            {manual && project && <ManualVolumeForm projectId={project.id} rates={summary?.rates || []} onClose={() => setManual(false)} onSaved={load} />}
            {realize && project && <RealizeForm project={project} ids={Array.from(selected)} realizations={realizations} onClose={() => setRealize(false)} onSaved={load} />}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Реализации (физический объём) и пул по ИП
// ---------------------------------------------------------------------------
const VolumesForm: React.FC<{ real: RealizationVolumes; rates: WorkRate[]; onClose: () => void; onSaved: () => void }> = ({ real, rates, onClose, onSaved }) => {
    const { showNotification } = useData();
    const [qty, setQty] = useState<Record<string, string>>(() => Object.fromEntries(real.volumes.map(v => [v.code, String(v.qty)])));
    const submit = async () => {
        try {
            await api.saveRealizationVolumes(real.id, Object.entries(qty).filter(([, v]) => Number(v) > 0).map(([code, v]) => ({ code, qty: Number(v) })));
            onSaved(); onClose();
        } catch (e) { showNotification(errorText(e), 'error'); }
    };
    return (
        <Modal isOpen onClose={onClose} title={`Объём реализации №${real.number} от ${formatDay(real.date)}`}>
            <div className="space-y-2 text-sm">
                <p className="text-xs text-slate-500">Точный физический объём, который заказчик принял по этой реализации. Из него — квота ИП по объекту для выплат самозанятым.</p>
                {rates.filter(r => r.role !== 'BUFFER').map(r => (
                    <div key={r.code} className="flex items-center gap-2">
                        <span className="flex-1">{r.name}</span>
                        <input type="number" min="0" step="0.001" className="w-28 rounded-md border border-slate-300 px-2 py-1 text-right"
                               value={qty[r.code] || ''} onChange={e => setQty(q => ({ ...q, [r.code]: e.target.value }))} />
                        <span className="w-10 text-slate-500">{r.unit}</span>
                    </div>
                ))}
                <div className="flex justify-end gap-2 pt-2 border-t">
                    <Button variant="secondary" size="sm" onClick={onClose}>Отмена</Button>
                    <Button size="sm" onClick={submit}>Сохранить</Button>
                </div>
            </div>
        </Modal>
    );
};

const PoolTab: React.FC<{ project: Project }> = ({ project }) => {
    const names = useNames();
    const [summary, setSummary] = useState<VolumeSummary | null>(null);
    const [reals, setReals] = useState<RealizationVolumes[]>([]);
    const [edit, setEdit] = useState<RealizationVolumes | null>(null);
    const load = useCallback(() => {
        api.volumeSummary(project.id).then(setSummary).catch(() => {});
        api.volumeRealizations(project.id).then(setReals).catch(() => {});
    }, [project.id]);
    useEffect(load, [load]);
    const rates = (summary?.rates || []).filter(r => r.role !== 'BUFFER');
    return (
        <div className="space-y-4">
            <Card title="Пул по ИП: Доступно = объём в реализациях ИП − уже выплачено самозанятым от этого ИП">
                <div className="overflow-x-auto"><table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500"><tr><Th>Позиция</Th><Th right>Выполнено (все заявки)</Th>
                        {summary?.pool.map(p => <Th key={p.organizationId} right>{p.organizationName.split(' ')[0]}: реализовано / выплачено / <b>доступно</b></Th>)}</tr></thead>
                    <tbody>{rates.map(r => (
                        <tr key={r.code} className="border-t">
                            <Td>{r.name}, {r.unit}</Td><Td right>{qtyText(summary?.done[r.code])}</Td>
                            {summary?.pool.map(p => { const v = p.positions[r.code]; return (
                                <Td key={p.organizationId} right className="whitespace-nowrap">{v ? <>{qtyText(v.received)} / {qtyText(v.allocated)} / <b className={v.available > 0 ? 'text-emerald-700' : ''}>{qtyText(v.available)}</b></> : '—'}</Td>); })}
                        </tr>))}</tbody>
                </table></div>
                {!summary?.pool.length && <p className="text-sm text-slate-500 mt-2">Квот пока нет: привяжите заявки к реализациям во вкладке «Заявки».</p>}
            </Card>
            <Card title="Реализации объекта">
                <div className="overflow-x-auto"><table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500"><tr><Th>ИП</Th><Th>№ / дата</Th><Th right>Сумма, ₽</Th><Th>Оплата</Th><Th>Заявок</Th><Th>Физический объём</Th><Th /></tr></thead>
                    <tbody>{reals.map(r => (
                        <tr key={r.id} className="border-t align-top">
                            <Td>{names.orgShort(r.organizationId)}</Td><Td className="whitespace-nowrap">№{r.number} от {formatDay(r.date)}</Td>
                            <Td right><Money value={r.gross} /></Td><Td><StatusBadge status={r.status} /></Td><Td>{r.requests}</Td>
                            <Td className="text-xs">{r.volumes.map(v => `${v.name}: ${qtyText(v.qty)}`).join(', ') || <span className="text-amber-600">объём не указан</span>}
                                {r.volumesAuto && <div className="text-slate-400">по строкам реализации — проверьте или укажите кнопкой «Объём»</div>}</Td>
                            <Td right><Button size="sm" variant="outline" onClick={() => setEdit(r)}>Объём</Button></Td>
                        </tr>))}
                        {!reals.length && <tr><Td colSpan={7} className="text-center text-slate-400">Нет реализаций с этим объектом</Td></tr>}
                    </tbody>
                </table></div>
                <p className="text-xs text-slate-500 mt-2">Реализацию можно создать здесь из заявок или в «Операциях» (кнопка «Реализация», с объектом). Если физический объём не указан,
                    он считается по строкам реализации: «…установке интерактивной панели» — панель и прошивка, «…с прокладкой кабеля до коммутатора» — ещё и линк до шкафа.
                    Старые оплаты разносятся по объектам полем «Объект» в карточке операции.</p>
            </Card>
            <PayoutsCard project={project} />
            {edit && <VolumesForm real={edit} rates={summary?.rates || []} onClose={() => setEdit(null)} onSaved={load} />}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Выплаты самозанятым по объёмам — проводятся в «Операциях» (карточка «Выплата самозанятому» с объектом),
// здесь — список с отметкой передачи в CRM и повторной отправкой
// ---------------------------------------------------------------------------
const PayoutsCard: React.FC<{ project: Project }> = ({ project }) => {
    const { showNotification } = useData();
    const names = useNames();
    const [payouts, setPayouts] = useState<VolumePayout[]>([]);
    const loadPayouts = useCallback(() => { api.volumePayouts(project.id).then(setPayouts).catch(() => {}); }, [project.id]);
    useEffect(loadPayouts, [loadPayouts]);
    const retry = async (id: string) => {
        try { const r = await api.retryCrmSync(id); showNotification(r.crmStatus === 'SENT' ? 'Передано в CRM' : r.crmError || 'Ошибка', r.crmStatus === 'SENT' ? 'success' : 'error'); loadPayouts(); }
        catch (e) { showNotification(errorText(e), 'error'); }
    };
    return (
        <Card title="Выплаты самозанятым по объёмам">
            <p className="text-xs text-slate-500 mb-2">Проводятся в «Операциях»: «Выплата самозанятому», объект — «{project.name}», кнопка «Подобрать объёмы».</p>
            <div className="overflow-x-auto"><table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500"><tr><Th>Дата</Th><Th>ИП</Th><Th>Самозанятый</Th><Th right>Сумма</Th><Th>Позиции</Th><Th>CRM</Th></tr></thead>
                <tbody>{payouts.map(p => (
                    <tr key={p.id} className="border-t align-top">
                        <Td>{formatDay(p.date)}</Td><Td>{names.orgShort(p.organizationId)}</Td><Td>{p.counterpartyName}</Td><Td right><Money value={p.amount} /></Td>
                        <Td className="text-xs">{p.lines.map(l => `${l.name} ${qtyText(l.qty)}`).join(', ')}</Td>
                        <Td>{p.crmStatus === 'SENT' ? <Badge variant="success">передано</Badge> : p.crmStatus === 'SKIPPED' ? <Badge>не передаётся</Badge> :
                            <span className="text-xs"><Badge variant="danger">{p.crmStatus === 'ERROR' ? 'ошибка' : 'не передано'}</Badge>
                                <button className="ml-1 text-blue-600" title={p.crmError || ''} onClick={() => retry(p.id)}><RefreshCw size={12} /></button>
                                {p.crmError && <div className="text-red-600 max-w-xs">{p.crmError}</div>}</span>}</Td>
                    </tr>))}
                    {!payouts.length && <tr><Td colSpan={6} className="text-center text-slate-400">Пока нет</Td></tr>}
                </tbody>
            </table></div>
        </Card>
    );
};

// ---------------------------------------------------------------------------
// Прайс объекта
// ---------------------------------------------------------------------------
const RatesTab: React.FC<{ project: Project }> = ({ project }) => {
    const { showNotification } = useData();
    const [rows, setRows] = useState<WorkRate[]>([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { api.workRates(project.id).then(setRows).catch(e => showNotification(errorText(e), 'error')); }, [project.id]);
    const set = (i: number, patch: Partial<WorkRate>) => setRows(rs => rs.map((r, j) => j === i ? { ...r, ...patch } : r));
    const save = async () => {
        try { setRows(await api.saveWorkRates(project.id, rows)); showNotification('Прайс сохранён', 'success'); }
        catch (e) { showNotification(errorText(e), 'error'); }
    };
    const cell = 'rounded-md border border-slate-300 px-2 py-1 text-sm';
    return (
        <Card title={`Прайс объекта «${project.name}»`}>
            <p className="text-xs text-slate-500 mb-2">Строки — параметры типа заявки в CRM («Настройки → Типы заявок → Параметры»): новый параметр
                появляется здесь с ценой 0, переименованный — с новым названием, удалённый — становится неактивным (история остаётся). Протяжка и обжим —
                из списания материалов склада. Цена заказчика — с НДС (как в таблице заказчика); позиции, которые заказчик не оплачивает, — с нулём. Роль задаёт пропорции акта
                (прошивок и линков не больше панелей) и подбор комплектов. «В задании и акте» — как позиция называется в документах самозанятому
                (пусто — как в CRM); одинаковые названия с одной ценой сливаются в одну строку. Снятая галочка «В объёмах» — позиция не входит
                ни в квоту ИП, ни в подбор.</p>
            <div className="overflow-x-auto"><table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500"><tr><Th>Наименование</Th><Th>Источник</Th><Th>Ед.</Th><Th right>Цена заказчика</Th><Th right>Цена монтажнику</Th><Th>Роль</Th><Th>В задании и акте</Th><Th>В объёмах</Th><Th /></tr></thead>
                <tbody>{rows.map((r, i) => (
                    <tr key={r.id || i} className={`border-t ${r.isActive === false ? 'opacity-60' : ''}`}>
                        <Td>{r.source === 'request_param' ? <span>{r.name}</span> :
                            <input className={`${cell} w-full min-w-[14rem]`} value={r.name} onChange={e => set(i, { name: e.target.value, ...(r.id || r.code ? {} : { code: `m${Date.now()}` }) })} />}
                            {r.isActive === false && <Badge variant="neutral">удалён в CRM</Badge>}</Td>
                        <Td className="text-xs text-slate-500 whitespace-nowrap">{SOURCES[r.source || 'manual']}</Td>
                        <Td><input className={`${cell} w-20`} value={r.unit} onChange={e => set(i, { unit: e.target.value })} /></Td>
                        <Td right><input type="number" min="0" step="0.01" className={`${cell} w-28 text-right`} value={r.clientPrice} onChange={e => set(i, { clientPrice: Number(e.target.value) })} /></Td>
                        <Td right><input type="number" min="0" step="0.01" className={`${cell} w-28 text-right`} value={r.contractorPrice} onChange={e => set(i, { contractorPrice: Number(e.target.value) })} /></Td>
                        <Td><select className={cell} value={r.role || ''} onChange={e => set(i, { role: (e.target.value || null) as RateRole })}>
                            {ROLES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select></Td>
                        <Td><input className={`${cell} w-full min-w-[16rem]`} value={r.docName || ''} placeholder={r.name}
                                   onChange={e => set(i, { docName: e.target.value })} /></Td>
                        <Td className="text-center"><input type="checkbox" checked={r.inVolumes !== false} onChange={e => set(i, { inVolumes: e.target.checked })}
                                   title="Учитывать позицию в объёмах (квота ИП, подбор)" /></Td>
                        <Td>{r.source !== 'request_param' &&
                            <button className="text-slate-400 hover:text-red-600" onClick={() => setRows(rs => rs.filter((_, j) => j !== i))}><Trash2 size={14} /></button>}</Td>
                    </tr>))}</tbody>
            </table></div>
            <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => setRows(rs => [...rs, { code: '', name: '', unit: 'шт.', clientPrice: 0, contractorPrice: 0, role: null, source: 'manual' }])}>
                    <Plus size={14} className="mr-1" /> Позиция вручную</Button>
                <Button size="sm" onClick={save}>Сохранить</Button>
            </div>
        </Card>
    );
};

// ---------------------------------------------------------------------------
// По адресам: сделано по заявкам ↔ выставлено заказчику (перечни адресов реализаций)
// ---------------------------------------------------------------------------
// Поправка по адресу: сделано по факту вместо заявок CRM (ошибка, дубль заявки) и перенос заявок на другой адрес
const AddressAdjustForm: React.FC<{ project: Project; row: AddressRow; rows: AddressRow[]; onClose: () => void; onSaved: (d: AddressLedger) => void }> =
    ({ project, row, rows, onClose, onSaved }) => {
    const { showNotification } = useData();
    const a = row.adjustment;
    const [panels, setPanels] = useState(a?.donePanels != null ? String(a.donePanels) : '');
    const [links, setLinks] = useState(a?.doneLinks != null ? String(a.doneLinks) : '');
    const [target, setTarget] = useState(a?.targetKey || '');
    const [comment, setComment] = useState(a?.comment || '');
    const save = async (clear = false) => {
        try {
            onSaved(await api.adjustAddress(project.id, { key: row.key, address: row.address,
                targetKey: clear ? null : target || null, donePanels: clear || panels === '' ? null : Number(panels),
                doneLinks: clear || links === '' ? null : Number(links), comment: clear ? null : comment || null }));
            onClose();
        } catch (e) { showNotification(errorText(e), 'error'); }
    };
    const others = rows.filter(x => x.key !== row.key && !x.movedTo && x.address);
    return (
        <Modal isOpen onClose={onClose} title="Поправка по адресу">
            <div className="space-y-3 text-sm">
                <div><div className="font-medium">{row.address}</div>{row.name && <div className="text-xs text-slate-500">{row.name}</div>}
                    <div className="text-xs text-slate-500 mt-1">По заявкам CRM: панели {qtyText(row.rawPanels)}, линки {qtyText(row.rawLinks)} ·
                        выставлено: панели {qtyText(row.billedPanels)}, линки {qtyText(row.billedLinks)}</div></div>
                <div className="grid grid-cols-2 gap-3">
                    <Input label="Сделано панелей по факту" type="number" min="0" step="1" value={panels} placeholder={`по заявкам: ${qtyText(row.rawPanels)}`}
                        onChange={e => setPanels(e.target.value)} />
                    <Input label="Линков до шкафа по факту" type="number" min="0" step="1" value={links} placeholder={`по заявкам: ${qtyText(row.rawLinks)}`}
                        onChange={e => setLinks(e.target.value)} />
                </div>
                <SearchSelect label="Засчитывать заявки этого адреса в адрес (панели увезли туда, где их выставили)" value={target} onChange={setTarget}
                    options={[{ value: '', label: '— не переносить —' }, ...others.map(x => ({ value: x.key, label: x.address }))]} />
                <Input label="Комментарий" value={comment} onChange={e => setComment(e.target.value)} placeholder="например: дубль заявки, ошибка монтажников" />
                <p className="text-xs text-slate-500">Пустые поля — как в заявках CRM. Поправка меняет только учёт в mb: заявки и баллы монтажников в CRM остаются как есть.</p>
                <div className="flex justify-between gap-2 pt-2 border-t">
                    {a ? <Button variant="outline" size="sm" onClick={() => save(true)}>Снять поправку</Button> : <span />}
                    <div className="flex gap-2"><Button variant="secondary" size="sm" onClick={onClose}>Отмена</Button>
                        <Button size="sm" onClick={() => save()}>Сохранить</Button></div>
                </div>
            </div>
        </Modal>
    );
};

const AddressesTab: React.FC<{ project: Project }> = ({ project }) => {
    const { showNotification } = useData();
    const [data, setData] = useState<AddressLedger | null>(null);
    const [state, setState] = useState<AddressState | ''>('');
    const [q, setQ] = useState('');
    const [edit, setEdit] = useState<AddressRow | null>(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const load = useCallback(() => { api.projectAddresses(project.id).then(setData).catch(e => showNotification(errorText(e), 'error')); }, [project.id]);
    useEffect(load, [load]);
    const sync = async () => {
        try { const r = await api.syncProjectAddresses(project.id); showNotification(`В реализацию: ${r.linked}, обратно в ожидание: ${r.unlinked}`, 'success'); load(); }
        catch (e) { showNotification(errorText(e), 'error'); }
    };
    if (!data) return null;
    const t = data.totals;
    const words = q.toLowerCase().replace(/ё/g, 'е').split(/\s+/).filter(Boolean);
    const rows = data.rows.filter(x => (!state || x.state === state)
        && words.every(w => `${x.address} ${x.name} ${x.requests.map(r => `${r.number} ${r.address}`).join(' ')}`.toLowerCase().replace(/ё/g, 'е').includes(w)));
    const states: AddressState[] = ['partial', 'unbilled', 'over', 'noRequests', 'moved', 'billed'];
    return (
        <div className="space-y-3">
            {!data.addressMode && <Card><p className="text-sm text-amber-700">У объекта нет реализаций с перечнем адресов. Загрузите адреса из таблицы заказчика
                в реализацию («Объекты» → реализация → «Загрузить объёмы из таблицы») — тогда заявки будут сверяться с выставленным по адресам.</p></Card>}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Card><div className="text-xs text-slate-500">Висит в объёмах (не выставлено), с НДС</div><div className="text-xl font-semibold text-red-600"><Money value={t.unbilled || 0} /> ₽</div></Card>
                <Card><div className="text-xs text-slate-500">Не выставлено панелей</div><div className="text-xl font-semibold">{qtyText(t.restPanels)}</div>
                    <div className="text-[11px] text-slate-400">по <Money value={data.pricePanel} /> ₽</div></Card>
                <Card><div className="text-xs text-slate-500">Не выставлено линков до шкафа</div><div className="text-xl font-semibold">{qtyText(t.restLinks)}</div>
                    <div className="text-[11px] text-slate-400">доплата по <Money value={data.priceLink} /> ₽</div></Card>
                <Card><div className="text-xs text-slate-500">Адресов</div><div className="text-xs space-y-0.5 mt-1">{states.map(s => <div key={s} className="flex justify-between gap-2">
                    <span>{ADDRESS_STATE[s].label}</span><span className="font-mono">{t['n_' + s] || 0}</span></div>)}</div></Card>
            </div>
            <div className="flex flex-wrap items-end gap-2">
                <div className="flex flex-wrap gap-1">{(['', ...states] as (AddressState | '')[]).map(s => (
                    <button key={s || 'all'} onClick={() => setState(s)}
                        className={`px-2.5 py-1 rounded-full text-xs border ${state === s ? 'bg-blue-50 border-blue-400 text-blue-700' : 'border-slate-300 text-slate-600'}`}>
                        {s ? `${ADDRESS_STATE[s].label} (${t['n_' + s] || 0})` : `Все (${data.rows.length})`}</button>))}</div>
                <div className="w-full sm:w-64"><Input placeholder="Поиск: адрес, школа, № заявки" value={q} onChange={e => setQ(e.target.value)} /></div>
                <Button size="sm" variant="outline" onClick={sync} title="Заявки выставленных полностью адресов — «В реализации», остальные — «Ждёт реализации»">
                    <RefreshCw size={14} className="mr-1" /> Пересчитать привязку заявок</Button>
            </div>
            <div className="overflow-x-auto bg-white border rounded-lg"><AddressTable rows={rows} onEdit={setEdit} /></div>
            {edit && <AddressAdjustForm project={project} row={edit} rows={data.rows} onClose={() => setEdit(null)} onSaved={setData} />}
            <p className="text-xs text-slate-500">Адреса сравниваются без «город Москва», района, «улица/дом/корпус» — «город Москва, улица Газопровод, дом 3А» = «Москва, Газопровод, 3А».
                Панели — заявки с ролью «Панель» в прайсе; линки — роль «Линк» с ценой заказчика (линк до шкафа). Выставлено: услуга «…панели» — панель,
                услуга «с прокладкой кабеля» — ещё и линк. «Висит» = остаток × цены последней таблицы заказчика (с НДС). «Выставлено больше» в долг не вычитается.</p>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Сверка (скачиваемый отчёт)
// ---------------------------------------------------------------------------
const ReportTab: React.FC<{ projects: Project[] }> = ({ projects }) => {
    const { state } = useData();
    const [customer, setCustomer] = useState('');
    const [projectId, setProjectId] = useState('');
    const [status, setStatus] = useState('done');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const customers = state.counterparties.filter(c => projects.some(p => p.customerId === c.id));
    const params = { customer_id: projectId ? undefined : customer || undefined, project_id: projectId || undefined, status,
                     date_from: from || undefined, date_to: to || undefined };
    return (
        <Card title="Сверка выполненных работ">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <Select label="Заказчик (все его объекты)" value={customer} onChange={e => { setCustomer(e.target.value); setProjectId(''); }}
                    options={[{ value: '', label: 'Все' }, ...customers.map(c => ({ value: c.id, label: c.name }))]} />
                <SearchSelect label="Или один объект" value={projectId} onChange={setProjectId}
                    options={[{ value: '', label: 'Все объекты заказчика' }, ...projects.filter(p => !customer || p.customerId === customer).map(p => ({ value: p.id, label: p.name }))]} />
                <Select label="Статус" value={status} onChange={e => setStatus(e.target.value)}
                    options={[{ value: 'done', label: 'Выполнено (всё физически сделанное)' }, { value: 'unpaid', label: 'Не оплачено заказчиком' }, { value: 'paid', label: 'Оплачено' }]} />
                <Input label="С" type="date" value={from} onChange={e => setFrom(e.target.value)} />
                <Input label="По" type="date" value={to} onChange={e => setTo(e.target.value)} />
            </div>
            <a className="inline-flex items-center gap-1 mt-3 rounded-md bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700" href={api.volumeReportUrl(params)}>
                <Download size={14} /> Скачать Excel</a>
            <p className="text-xs text-slate-500 mt-2">«Выполнено» — все закрытые заявки; «Не оплачено» — без реализации или реализация не оплачена; «Оплачено» — реализация оплачена полностью.
                Суммы — оценка по прайсу заказчика.</p>
        </Card>
    );
};

// ---------------------------------------------------------------------------
// Настройки обмена с CRM: типы заявок → объект, таблица соответствий
// ---------------------------------------------------------------------------
const SettingsTab: React.FC<{ projects: Project[] }> = ({ projects }) => {
    const { showNotification } = useData();
    const [s, setS] = useState<VolumeSettings | null>(null);
    const [maps, setMaps] = useState<LanMapping[]>([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const load = useCallback(() => { api.volumeSettings().then(x => { setS(x); setMaps(x.mappings); }).catch(e => showNotification(errorText(e), 'error')); }, []);
    useEffect(load, [load]);
    const saveType = async (t: LanType, patch: Partial<LanType>) => {
        const next = { ...t, ...patch };
        try { await api.saveLanType(t.id, { projectId: next.projectId, mode: next.mode, manDayRate: next.manDayRate }); load(); }
        catch (e) { showNotification(errorText(e), 'error'); }
    };
    const saveMaps = async () => {
        try { setMaps(await api.saveMappings(maps)); showNotification('Таблица соответствий сохранена, заявки пересчитаны', 'success'); load(); }
        catch (e) { showNotification(errorText(e), 'error'); }
    };
    const cell = 'rounded-md border border-slate-300 px-2 py-1 text-sm';
    if (!s) return null;
    return (
        <div className="space-y-4">
            {(!s.integrationKey || !s.crmConfigured) && <Card><p className="text-sm text-amber-700">
                {!s.integrationKey && 'Ключ обмена LAN_INSTALL_API_KEY не задан — CRM не сможет присылать заявки и сотрудников. '}
                {!s.crmConfigured && 'Адрес CRM (LAN_INSTALL_CRM_URL) не задан — выплаты не будут передаваться в зарплатный модуль.'}</p></Card>}
            <Card title="Типы заявок CRM">
                <div className="overflow-x-auto"><table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500"><tr><Th>Тип в CRM</Th><Th>Объект mb</Th><Th>Расчёт суммы заказчику</Th><Th>Ставка в день на человека, ₽</Th></tr></thead>
                    <tbody>{s.types.map(t => (
                        <tr key={t.id} className="border-t">
                            <Td>{t.name} <span className="text-slate-400">#{t.crmTypeId}</span></Td>
                            <Td><div className="min-w-[16rem]"><SearchSelect className="py-1" value={t.projectId || ''} onChange={v => saveType(t, { projectId: v || null })}
                                options={[{ value: '', label: '— без объекта (разбор) —' }, ...projects.map(p => ({ value: p.id, label: p.name }))]} /></div></Td>
                            <Td><select className={cell} value={t.mode} onChange={e => saveType(t, { mode: e.target.value as LanType['mode'] })}>
                                {MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}</select></Td>
                            <Td>{t.mode === 'MAN_DAY' && <input type="number" min="0" className={`${cell} w-28 text-right`} defaultValue={t.manDayRate ?? ''}
                                onBlur={e => saveType(t, { manDayRate: e.target.value === '' ? null : Number(e.target.value) })} />}</Td>
                        </tr>))}
                        {!s.types.length && <tr><Td colSpan={4} className="text-center text-slate-400">Типы появятся с первой заявкой из CRM</Td></tr>}
                    </tbody>
                </table></div>
            </Card>
            <Card title="Материалы склада → позиции прайса">
                <p className="text-xs text-slate-500 mb-2">Работы заявки приходят параметрами типа заявки один в один, без соответствий. Здесь — только
                    списанные со склада материалы: кабель UTP (метры) → «Протяжка кабеля UTP», коннекторы RJ-45 (шт.) → «Обжим коннектора RJ-45».
                    Количество = списано × коэффициент; позиция пусто — не учитывать.</p>
                <datalist id="vol-codes">{s.codes.map(c => <option key={c} value={c} />)}</datalist>
                <div className="overflow-x-auto"><table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500"><tr><Th>Материал (как на складе)</Th><Th>Позиция (код)</Th><Th right>Коэффициент</Th><Th /></tr></thead>
                    <tbody>{maps.map((m, i) => (
                        <tr key={i} className="border-t">
                            <Td><input className={`${cell} w-full min-w-[14rem]`} value={m.sourceName} onChange={e => setMaps(ms => ms.map((x, j) => j === i ? { ...x, sourceName: e.target.value } : x))} /></Td>
                            <Td><input list="vol-codes" className={`${cell} w-40`} value={m.code || ''} onChange={e => setMaps(ms => ms.map((x, j) => j === i ? { ...x, code: e.target.value || null } : x))} /></Td>
                            <Td right><input type="number" min="0" step="0.0001" className={`${cell} w-24 text-right`} value={m.factor} onChange={e => setMaps(ms => ms.map((x, j) => j === i ? { ...x, factor: Number(e.target.value) } : x))} /></Td>
                            <Td><button className="text-slate-400 hover:text-red-600" onClick={() => setMaps(ms => ms.filter((_, j) => j !== i))}><Trash2 size={14} /></button></Td>
                        </tr>))}</tbody>
                </table></div>
                {s.unmapped.length > 0 && <div className="mt-2 text-xs"><span className="text-amber-700">Не сопоставлено (пришло со склада): </span>
                    {s.unmapped.map(u => <button key={u.source + u.sourceName} className="mr-2 text-blue-600 hover:underline"
                        onClick={() => setMaps(ms => [...ms, { source: 'MATERIAL', sourceName: u.sourceName, code: null, factor: 1 }])}>+ {u.sourceName}</button>)}</div>}
                <div className="flex gap-2 mt-3">
                    <Button size="sm" variant="outline" onClick={() => setMaps(ms => [...ms, { source: 'MATERIAL', sourceName: '', code: null, factor: 1 }])}><Plus size={14} className="mr-1" /> Строка</Button>
                    <Button size="sm" onClick={saveMaps}>Сохранить и пересчитать заявки</Button>
                </div>
            </Card>
        </div>
    );
};

// ---------------------------------------------------------------------------
export const Volumes: React.FC = () => {
    const projects = useProjects();
    const [params] = useSearchParams();  // ссылка из сверки по объектам: ?tab=addresses&project=…
    // Сохранённая вкладка могла остаться от убранной «Выплаты самозанятому» — тогда «Реализации и пул»
    const [tab, setTab] = useState<Tab>(() => { const p = params.get('tab'); if (TABS.some(x => x.key === p)) return p as Tab; try { const t = localStorage.getItem('volumes.tab'); return TABS.some(x => x.key === t) ? t as Tab : t === 'payout' ? 'pool' : 'requests'; } catch { return 'requests'; } });
    const [projectId, setProjectId] = useState<string>(() => { if (params.get('project')) return params.get('project') as string; try { return localStorage.getItem('volumes.project') || ''; } catch { return ''; } });
    useEffect(() => { try { localStorage.setItem('volumes.tab', tab); localStorage.setItem('volumes.project', projectId); } catch { /* приватный режим */ } }, [tab, projectId]);
    useEffect(() => {
        if (!projectId && projects.length) setProjectId((projects.find(p => p.name === 'Монтаж панелей') || projects[0]).id);
    }, [projects, projectId]);
    const project = useMemo(() => projects.find(p => p.id === projectId) || null, [projects, projectId]);
    const needsProject = tab !== 'settings' && tab !== 'report';
    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-xl font-semibold text-slate-900">Объёмы работ</h1>
                    <p className="text-xs text-slate-500">Заявки CRM → реализации по ИП → выплаты самозанятым из квоты своего ИП</p>
                </div>
                {needsProject && <div className="w-full sm:w-72"><SearchSelect label="Объект" value={projectId} onChange={setProjectId}
                    options={projects.map(p => ({ value: p.id, label: p.name }))} /></div>}
            </div>
            <div className="flex gap-1 overflow-x-auto border-b">
                {TABS.map(t => <button key={t.key} onClick={() => setTab(t.key)}
                    className={`whitespace-nowrap px-3 py-2 text-sm border-b-2 -mb-px ${tab === t.key ? 'border-blue-600 text-blue-700 font-medium' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>{t.label}</button>)}
            </div>
            {tab === 'requests' && <RequestsTab project={project} projects={projects} />}
            {tab === 'addresses' && project && <AddressesTab project={project} />}
            {tab === 'pool' && project && <PoolTab project={project} />}
            {tab === 'rates' && project && <RatesTab project={project} />}
            {tab === 'report' && <ReportTab projects={projects} />}
            {tab === 'settings' && <SettingsTab projects={projects} />}
        </div>
    );
};
